import React, { useState, useMemo, useRef, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Card, MetricValue } from '../finance/components/Card';
import DateRangePicker from '../finance/components/DateRangePicker';
import { getDatePresetLabel } from '../finance/data/mockData';
import SalesDetailPanel from './SalesDetailPanel';
import { giftCardService } from '../../_services/giftCard.service';
import { isSaleUnclaimed } from './data';

// SalesPage — voucher sales overview.
//
// Renders five sections top-to-bottom: sticky filter bar, KPI row, area
// chart with channel split, attention zone (expiring + unclaimed),
// two-column breakdown (top designs + channel donut), and the
// transactions table with row-click → SalesDetailPanel.
//
// Reuses Finance's Card / MetricValue / DateRangePicker so the visual
// language matches the rest of Beu's analytics. Recharts is already a
// dependency via Finance.
//
// Phase 1 reads from in-memory mock data (SAMPLE_VOUCHER_SALES, seeded
// in data.js). Backend hookup lands later.

// Preset → date range. Identical logic to the helper in Finance's
// OverviewView; duplicated rather than imported so V2 doesn't reach
// into Finance internals (it's small and unlikely to drift).
const computePresetRange = (preset) => {
  const now = new Date();
  let startDate, endDate;
  switch (preset) {
    case 'today':       startDate = endDate = new Date(); break;
    case 'yesterday': { const d = new Date(); d.setDate(d.getDate() - 1); startDate = endDate = d; break; }
    case 'last7':       endDate = new Date(); startDate = new Date(); startDate.setDate(startDate.getDate() - 7); break;
    case 'last4weeks':  endDate = new Date(); startDate = new Date(); startDate.setDate(startDate.getDate() - 28); break;
    case 'last6months': endDate = new Date(); startDate = new Date(); startDate.setMonth(startDate.getMonth() - 6); break;
    case 'last12months':endDate = new Date(); startDate = new Date(); startDate.setFullYear(startDate.getFullYear() - 1); break;
    case 'wtd':         endDate = new Date(); startDate = new Date(now); startDate.setDate(startDate.getDate() - startDate.getDay()); break;
    case 'mtd':         endDate = new Date(); startDate = new Date(now.getFullYear(), now.getMonth(), 1); break;
    case 'qtd': {
      endDate = new Date();
      const q = Math.floor(now.getMonth() / 3);
      startDate = new Date(now.getFullYear(), q * 3, 1);
      break;
    }
    case 'ytd':         endDate = new Date(); startDate = new Date(now.getFullYear(), 0, 1); break;
    case 'all':         endDate = new Date(); startDate = new Date(2020, 0, 1); break;
    default:            endDate = new Date(); startDate = new Date(now.getFullYear(), now.getMonth(), 1);
  }
  return { startDate, endDate };
};

const fmtMoney = (n, currency = '£') => `${currency}${Math.round(n).toLocaleString('en-GB')}`;
const fmtCount = (n) => Number(n).toLocaleString('en-GB');

// Status pill colours mirror Finance's transaction badges so analytics
// reads consistently across the app.
const STATUS_PILL = {
  active:   { label: 'Active',   bg: '#d1fae5', color: '#059669' },
  redeemed: { label: 'Redeemed', bg: '#f3f4f6', color: '#374151' },
  partial:  { label: 'Partial',  bg: '#fef3c7', color: '#b45309' },
  expired:  { label: 'Expired',  bg: '#fee2e2', color: '#dc2626' },
};

// CSV of the currently-filtered rows. Includes the voucher code so the
// salon can reconcile with POS receipts.
const exportSalesCsv = (rows, currency = '£') => {
  const headers = [
    'Date', 'Code', 'Recipient name', 'Recipient email',
    'Buyer name', 'Buyer email', 'Voucher', 'Face value',
    'Paid', 'Redeemed', 'Channel', 'Status', 'Expires',
  ];
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(',')];
  rows.forEach((s) => {
    lines.push([
      s.soldAt, s.code,
      s.recipientName, s.recipientEmail,
      s.buyerName, s.buyerEmail,
      s.voucherId,
      `${currency}${s.faceValue}`,
      `${currency}${s.paidAmount}`,
      `${currency}${s.redeemedAmount}`,
      s.channel, s.status, s.expiresAt,
    ].map(escape).join(','));
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `voucher-sales-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

const ChevronDownIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2 4l3 3 3-3" />
  </svg>
);
const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M7 1v8M3.5 6L7 9.5 10.5 6M2 12h10" />
  </svg>
);
const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden>
    <circle cx="7" cy="7" r="5" />
    <path d="M10.5 10.5L14 14" />
  </svg>
);

const SalesPage = ({
  vouchers = [],
  designs = [],
  currency = '£',
  salonName = '',
  onResendEmail,
  onNotifyExpiring,
  onAttachClient,
}) => {
  // Notify-expiring action state.
  const [notifying, setNotifying] = useState(false);
  const [notifyDone, setNotifyDone] = useState(false);
  const handleNotifyExpiring = async () => {
    if (!onNotifyExpiring || notifying) return;
    setNotifying(true);
    try {
      await onNotifyExpiring(30);
      setNotifyDone(true);
      setTimeout(() => setNotifyDone(false), 4000);
    } catch (e) {
      console.error('[Sales] Notify expiring failed', e);
    } finally {
      setNotifying(false);
    }
  };

  // ─── Filter state ───
  const [dateFilter, setDateFilter] = useState('last12months');
  const initialRange = useMemo(() => computePresetRange('last12months'), []);
  const [dateStart, setDateStart] = useState(initialRange.startDate);
  const [dateEnd, setDateEnd] = useState(initialRange.endDate);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [granularity, setGranularity] = useState('monthly');
  const [granularityOpen, setGranularityOpen] = useState(false);
  const [channelFilter, setChannelFilter] = useState('all');
  const [chartMode, setChartMode] = useState('revenue'); // 'revenue' | 'count'
  const [unclaimedOnly, setUnclaimedOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSale, setSelectedSale] = useState(null);
  const tableRef = useRef(null);

  // ─── Server-driven data ───
  // Aggregates (KPIs / chart / breakdowns / attention) come from the
  // sales-dashboard endpoint and the table is server-paginated, so the full
  // instance set never ships to the client — the tab stays fast no matter how
  // many vouchers a salon sells.
  const PAGE_SIZE = 25;
  const [dashboard, setDashboard] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Bumped after a detail-panel action mutates a sale → refetch everything.
  const [refreshTick, setRefreshTick] = useState(0);

  // Debounce the free-text search so we don't query per keystroke.
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const tableFilters = useMemo(() => ({
    startDate: dateStart ? dateStart.toISOString() : undefined,
    endDate: dateEnd ? dateEnd.toISOString() : undefined,
    channel: channelFilter !== 'all' ? channelFilter : undefined,
    unclaimed: unclaimedOnly || undefined,
    search: debouncedQuery || undefined,
  }), [dateStart, dateEnd, channelFilter, unclaimedOnly, debouncedQuery]);

  // First page whenever the table filters change (or after a mutation).
  useEffect(() => {
    let alive = true;
    giftCardService.listSales({ ...tableFilters, skip: 0, limit: PAGE_SIZE })
      .then((r) => { if (alive) { setRows(r.sales); setRowsTotal(r.total); } })
      .catch((e) => console.error('[Sales] rows fetch failed', e));
    return () => { alive = false; };
  }, [tableFilters, refreshTick]);

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await giftCardService.listSales({ ...tableFilters, skip: rows.length, limit: PAGE_SIZE });
      setRows((prev) => [...prev, ...r.sales]);
      setRowsTotal(r.total);
    } catch (e) {
      console.error('[Sales] load more failed', e);
    } finally {
      setLoadingMore(false);
    }
  };

  // Aggregates whenever the analytics scope changes.
  useEffect(() => {
    let alive = true;
    giftCardService.getSalesDashboard({
      startDate: dateStart ? dateStart.toISOString() : undefined,
      endDate: dateEnd ? dateEnd.toISOString() : undefined,
      channel: channelFilter !== 'all' ? channelFilter : undefined,
      granularity,
    })
      .then((d) => { if (alive) setDashboard(d); })
      .catch((e) => console.error('[Sales] dashboard fetch failed', e));
    return () => { alive = false; };
  }, [dateStart, dateEnd, channelFilter, granularity, refreshTick]);

  // CSV export pulls the full filtered set on demand (capped server-side) —
  // the table itself only ever holds the pages the user has revealed.
  const handleExportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const r = await giftCardService.listSales({ ...tableFilters, skip: 0, limit: 5000 });
      exportSalesCsv(r.sales, currency);
    } catch (e) {
      console.error('[Sales] export failed', e);
    } finally {
      setExporting(false);
    }
  };

  // ─── Derived view-model (same names/shapes the render always used) ───
  const kpis = useMemo(() => {
    const cur = (dashboard && dashboard.current) || { count: 0, revenue: 0, face: 0, redeemed: 0, outstanding: 0 };
    const prev = dashboard && dashboard.previous;
    const rate = (t) => (t.face > 0 ? Math.round((t.redeemed / t.face) * 100) : 0);
    const base = { count: cur.count, revenue: cur.revenue, outstanding: cur.outstanding, redemptionRate: rate(cur) };
    if (!prev) return base;
    const pct = (c, p) => (p === 0 ? (c === 0 ? 0 : 100) : Math.round(((c - p) / p) * 100));
    return {
      ...base,
      countChange: pct(cur.count, prev.count),
      revenueChange: pct(cur.revenue, prev.revenue),
      outstandingChange: pct(cur.outstanding, prev.outstanding),
      redemptionRateChange: base.redemptionRate - rate(prev),
    };
  }, [dashboard]);

  const chartData = useMemo(() => ((dashboard && dashboard.series) || []).map((bkt) => {
    const d = bkt.sampleDate ? new Date(bkt.sampleDate) : new Date();
    const label = granularity === 'monthly'
      ? d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
      : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    return { key: bkt._id, label, total: bkt.total, count: bkt.count, online: bkt.online, pos: bkt.pos, sampleDate: d };
  }), [dashboard, granularity]);

  const topVouchers = useMemo(() => ((dashboard && dashboard.topVouchers) || []).map((t) => ({
    voucherId: t.voucherId,
    count: t.count,
    revenue: t.revenue,
    voucher: vouchers.find((v) => String(v.id) === String(t.voucherId)) || (t.name ? { name: t.name } : null),
  })), [dashboard, vouchers]);

  const channelBreakdown = useMemo(() => {
    const get = (id) => ((dashboard && dashboard.channels) || []).find((c) => c._id === id) || { count: 0, revenue: 0 };
    const online = get('online');
    const pos = get('pos');
    const total = online.revenue + pos.revenue;
    const pc = (r) => (total > 0 ? Math.round((r / total) * 100) : 0);
    return {
      online: { count: online.count, revenue: online.revenue, percent: pc(online.revenue) },
      pos: { count: pos.count, revenue: pos.revenue, percent: pc(pos.revenue) },
      total,
    };
  }, [dashboard]);

  const expiringCount = (dashboard && dashboard.expiringCount) || 0;
  const expiringValue = (dashboard && dashboard.expiringValue) || 0;
  const unclaimedCount = (dashboard && dashboard.unclaimedCount) || 0;
  const tableRows = rows;

  const selectedVoucher = selectedSale ? (vouchers.find(v => v.id === selectedSale.voucherId) || null) : null;
  const selectedDesign  = selectedSale ? (designs.find(d => d.id === selectedSale.designId) || null) : null;

  // "Review unclaimed" — applies the filter and scrolls the table into view.
  const reviewUnclaimed = () => {
    setUnclaimedOnly(true);
    setSearchQuery('');
    if (tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const dateLabel = dateFilter === null && dateStart && dateEnd
    ? `${dateStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${dateEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
    : getDatePresetLabel(dateFilter);

  // KPI badge helpers.
  const pctChange = (v) => v == null ? undefined : `${v >= 0 ? '+' : ''}${v}%`;
  const pctType = (v) => v == null ? 'neutral' : (v > 0 ? 'positive' : (v < 0 ? 'negative' : 'neutral'));
  // Outstanding is "good when lower" — invert sign for the badge colour.
  const outstandingType = (v) => v == null ? 'neutral' : (v > 0 ? 'negative' : (v < 0 ? 'positive' : 'neutral'));
  const ptChange = (v) => v == null ? undefined : `${v >= 0 ? '+' : ''}${v}pt`;

  return (
    <>
      {/* ─── FILTER BAR ─── */}
      <div className="v2-sales-filterbar">
        <div className="v2-sales-fb-group">
          {/* Date range — reuses Finance's DateRangePicker */}
          <div className="v2-sales-fb-anchor">
            <button
              type="button"
              className="v2-sales-fb-btn"
              onClick={() => setDatePickerOpen((o) => !o)}
            >
              <span className="v2-sales-fb-label">Date range</span>
              <span className="v2-sales-fb-sep" />
              <span className="v2-sales-fb-val">{dateLabel}</span>
              <ChevronDownIcon />
            </button>
            <DateRangePicker
              isOpen={datePickerOpen}
              onClose={() => setDatePickerOpen(false)}
              startDate={dateStart}
              endDate={dateEnd}
              selectedPreset={dateFilter}
              onPresetChange={(p) => {
                setDateFilter(p);
                const r = computePresetRange(p);
                setDateStart(r.startDate);
                setDateEnd(r.endDate);
              }}
              onApply={(s, e) => { setDateStart(s); setDateEnd(e); setDateFilter(null); }}
            />
          </div>

          {/* Granularity */}
          <div className="v2-sales-fb-anchor">
            <button
              type="button"
              className="v2-sales-fb-btn"
              onClick={() => setGranularityOpen((o) => !o)}
            >
              <span className="v2-sales-fb-val">{granularity[0].toUpperCase() + granularity.slice(1)}</span>
              <ChevronDownIcon />
            </button>
            {granularityOpen && (
              <>
                <div className="v2-sales-fb-backdrop" onClick={() => setGranularityOpen(false)} />
                <div className="v2-sales-fb-menu">
                  {['daily', 'weekly', 'monthly'].map((g) => (
                    <button
                      key={g}
                      type="button"
                      className={`v2-sales-fb-menu-item ${granularity === g ? 'is-active' : ''}`}
                      onClick={() => { setGranularity(g); setGranularityOpen(false); }}
                    >
                      {g[0].toUpperCase() + g.slice(1)}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Channel filter chips */}
          <div className="v2-sales-fb-chips">
            {[
              { id: 'all', label: 'All channels' },
              { id: 'online', label: 'Online' },
              { id: 'pos', label: 'POS' },
            ].map((c) => (
              <button
                key={c.id}
                type="button"
                className={`v2-sales-fb-chip ${channelFilter === c.id ? 'is-active' : ''}`}
                onClick={() => setChannelFilter(c.id)}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="v2-sales-fb-right">
          <button
            type="button"
            className="v2-sales-fb-btn v2-sales-fb-btn--export"
            onClick={handleExportCsv}
            disabled={exporting || rowsTotal === 0}
          >
            <DownloadIcon />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* ─── KPI ROW ─── */}
      <div className="v2-sales-kpi-row">
        <Card>
          <p className="v2-sales-kpi-label">Sold</p>
          <MetricValue
            value={fmtCount(kpis.count)}
            change={pctChange(kpis.countChange)}
            changeType={pctType(kpis.countChange)}
            subtitle="vs previous period"
          />
        </Card>
        <Card>
          <p className="v2-sales-kpi-label">Revenue</p>
          <MetricValue
            value={fmtMoney(kpis.revenue, currency)}
            change={pctChange(kpis.revenueChange)}
            changeType={pctType(kpis.revenueChange)}
            subtitle="from voucher sales"
          />
        </Card>
        <Card>
          <p className="v2-sales-kpi-label">Outstanding</p>
          <MetricValue
            value={fmtMoney(kpis.outstanding, currency)}
            change={pctChange(kpis.outstandingChange)}
            changeType={outstandingType(kpis.outstandingChange)}
            subtitle="unredeemed balance"
          />
        </Card>
        <Card>
          <p className="v2-sales-kpi-label">Redemption</p>
          <MetricValue
            value={`${kpis.redemptionRate}%`}
            change={ptChange(kpis.redemptionRateChange)}
            changeType={pctType(kpis.redemptionRateChange)}
            subtitle="of face value used"
          />
        </Card>
      </div>

      {/* ─── CHART ─── */}
      <div className="v2-sales-chart-card">
        <Card>
          <div className="v2-sales-chart-header">
            <h3 className="v2-sales-card-title">
              {chartMode === 'revenue' ? 'Revenue' : 'Sales count'} over time
            </h3>
            <div className="v2-ed-segment" role="tablist" aria-label="Chart mode">
              {['revenue', 'count'].map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`v2-ed-segment-btn ${chartMode === m ? 'active' : ''}`}
                  onClick={() => setChartMode(m)}
                >
                  {m[0].toUpperCase() + m.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="v2-sales-chart-body">
            {chartData.length === 0 ? (
              <p className="v2-sales-empty">No sales in this period.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <defs>
                    <linearGradient id="v2sales-online" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#007EA0" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#007EA0" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="v2sales-pos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#14b8a6" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#14b8a6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="label" tick={{ fill: '#737373', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis
                    tick={{ fill: '#737373', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={48}
                    tickFormatter={(v) => chartMode === 'revenue'
                      ? `${currency}${v >= 1000 ? `${Math.round(v / 100) / 10}k` : v}`
                      : v}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: 8, padding: '8px 12px' }}
                    itemStyle={{ color: '#fff', fontSize: 12 }}
                    labelStyle={{ color: '#9ca3af', fontSize: 11, marginBottom: 4 }}
                    formatter={(v, n) => [
                      chartMode === 'revenue' ? fmtMoney(v, currency) : v,
                      n === 'online' ? 'Online' : n === 'pos' ? 'POS' : 'Sales',
                    ]}
                  />
                  {chartMode === 'revenue' ? (
                    <>
                      <Area type="monotone" dataKey="online" stackId="1" stroke="#007EA0" fill="url(#v2sales-online)" strokeWidth={2} />
                      <Area type="monotone" dataKey="pos"    stackId="1" stroke="#14b8a6" fill="url(#v2sales-pos)"    strokeWidth={2} />
                    </>
                  ) : (
                    <Area type="monotone" dataKey="count" stroke="#007EA0" fill="url(#v2sales-online)" strokeWidth={2} />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>

      {/* ─── ATTENTION ZONE ─── */}
      {(expiringCount > 0 || unclaimedCount > 0) && (
        <div className="v2-sales-attention">
          {expiringCount > 0 && (
            <div className="v2-sales-attention-row">
              <span className="v2-sales-attention-icon" aria-hidden>⏰</span>
              <span className="v2-sales-attention-text">
                <strong>
                  {expiringCount} {expiringCount === 1 ? 'voucher' : 'vouchers'}{' '}
                  worth {fmtMoney(expiringValue, currency)}
                </strong>
                {' '}expire in the next 30 days
              </span>
              {onNotifyExpiring && (
              <button
                type="button"
                className="v2-sales-attention-cta"
                onClick={handleNotifyExpiring}
                disabled={notifying}
              >
                {notifying ? 'Sending…' : (notifyDone ? 'Sent ✓' : 'Notify recipients')}
              </button>
              )}
            </div>
          )}
          {unclaimedCount > 0 && (
            <div className="v2-sales-attention-row">
              <span className="v2-sales-attention-icon" aria-hidden>⚠️</span>
              <span className="v2-sales-attention-text">
                <strong>{unclaimedCount} {unclaimedCount === 1 ? 'sale' : 'sales'} unclaimed</strong>
                {' '}so POS sales with no client attached
              </span>
              <button type="button" className="v2-sales-attention-cta" onClick={reviewUnclaimed}>
                Review unclaimed
              </button>
            </div>
          )}
        </div>
      )}

      {/* ─── TWO-COLUMN BREAKDOWN ─── */}
      <div className="v2-sales-breakdown">
        <Card>
          <h3 className="v2-sales-card-title">Top selling vouchers</h3>
          {topVouchers.length === 0 ? (
            <p className="v2-sales-empty">No data in this period.</p>
          ) : (
            <div className="v2-sales-top-list">
              {topVouchers.map((row, i) => {
                const max = topVouchers[0].revenue || 1;
                const pct = Math.max(8, Math.round((row.revenue / max) * 100));
                const name = row.voucher ? row.voucher.name : row.voucherId;
                return (
                  // Sales with no linked voucher group under an undefined id, so
                  // fall back to the index to keep the key unique.
                  <div key={row.voucherId || `top-${i}`} className="v2-sales-top-row">
                    <div className="v2-sales-top-info">
                      <span className="v2-sales-top-name">{name || row.voucherId}</span>
                      <span className="v2-sales-top-count">{row.count} sold</span>
                    </div>
                    <div className="v2-sales-top-bar">
                      <div className="v2-sales-top-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="v2-sales-top-revenue">{fmtMoney(row.revenue, currency)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
        <Card>
          <h3 className="v2-sales-card-title">Channel breakdown</h3>
          {channelBreakdown.total === 0 ? (
            <p className="v2-sales-empty">No data in this period.</p>
          ) : (
            <div className="v2-sales-channel">
              <div className="v2-sales-channel-chart">
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Online', value: channelBreakdown.online.revenue },
                        { name: 'POS',    value: channelBreakdown.pos.revenue },
                      ]}
                      dataKey="value"
                      innerRadius={48}
                      outerRadius={72}
                      paddingAngle={2}
                    >
                      <Cell fill="#007EA0" />
                      <Cell fill="#14b8a6" />
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="v2-sales-channel-legend">
                <div className="v2-sales-channel-leg-row">
                  <span className="v2-sales-channel-swatch" style={{ background: '#007EA0' }} />
                  <span className="v2-sales-channel-label">Online</span>
                  <span className="v2-sales-channel-pct">{channelBreakdown.online.percent}%</span>
                  <span className="v2-sales-channel-meta">{fmtMoney(channelBreakdown.online.revenue, currency)}</span>
                </div>
                <div className="v2-sales-channel-leg-row">
                  <span className="v2-sales-channel-swatch" style={{ background: '#14b8a6' }} />
                  <span className="v2-sales-channel-label">POS</span>
                  <span className="v2-sales-channel-pct">{channelBreakdown.pos.percent}%</span>
                  <span className="v2-sales-channel-meta">{fmtMoney(channelBreakdown.pos.revenue, currency)}</span>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* ─── TRANSACTIONS TABLE ─── */}
      <div className="v2-sales-table-section" ref={tableRef}>
        <div className="v2-sales-table-header">
          <h3 className="v2-sales-card-title">Transactions</h3>
          <div className="v2-sales-table-tools">
            <label className="v2-txn-search">
              <span className="v2-txn-search-icon"><SearchIcon /></span>
              <input
                type="text"
                className="v2-txn-search-input"
                placeholder="Search by name, email or code…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </label>
            <button
              type="button"
              className={`v2-sales-unclaimed-toggle ${unclaimedOnly ? 'is-active' : ''}`}
              onClick={() => setUnclaimedOnly((u) => !u)}
            >
              Unclaimed only
            </button>
          </div>
        </div>

        {tableRows.length === 0 ? (
          <div className="v2-sales-empty-state">
            <p>No sales match the current filters.</p>
          </div>
        ) : (
          <>
            <table className="v2-sales-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Recipient</th>
                  <th>Voucher</th>
                  <th className="v2-sales-tbl-right">Amount</th>
                  <th>Channel</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((s) => {
                  const status = STATUS_PILL[s.status] || STATUS_PILL.active;
                  const voucher = vouchers.find(v => v.id === s.voucherId);
                  const unclaimed = isSaleUnclaimed(s);
                  return (
                    <tr
                      key={s.id}
                      className="v2-sales-table-row"
                      onClick={() => setSelectedSale(s)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') setSelectedSale(s); }}
                    >
                      <td>{new Date(s.soldAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}</td>
                      <td>
                        {unclaimed ? (
                          <span className="v2-sales-tbl-unclaimed-cell">—</span>
                        ) : (
                          <>
                            <div className="v2-sales-tbl-name">{s.recipientName || s.buyerName || '—'}</div>
                            {(s.recipientEmail || s.buyerEmail) && (
                              <div className="v2-sales-tbl-sub">{s.recipientEmail || s.buyerEmail}</div>
                            )}
                          </>
                        )}
                      </td>
                      <td>
                        <div className="v2-sales-tbl-name">{voucher ? voucher.name : s.voucherId}</div>
                        <div className="v2-sales-tbl-sub v2-sales-tbl-code">{s.code}</div>
                      </td>
                      <td className="v2-sales-tbl-right">
                        <strong>{fmtMoney(s.paidAmount, currency)}</strong>
                        {s.paidAmount < s.faceValue && (
                          <div className="v2-sales-tbl-sub">face {fmtMoney(s.faceValue, currency)}</div>
                        )}
                      </td>
                      <td>
                        <span className="v2-sales-tbl-channel">{s.channel === 'pos' ? 'POS' : 'Online'}</span>
                      </td>
                      <td>
                        {unclaimed ? (
                          <span className="v2-sales-tbl-pill" style={{ background: '#fef3c7', color: '#b45309' }}>Unclaimed</span>
                        ) : (
                          <span className="v2-sales-tbl-pill" style={{ background: status.bg, color: status.color }}>{status.label}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile card list — same data, CSS-switched at narrow widths */}
            <div className="v2-sales-table-mobile">
              {tableRows.map((s) => {
                const status = STATUS_PILL[s.status] || STATUS_PILL.active;
                const voucher = vouchers.find(v => v.id === s.voucherId);
                const unclaimed = isSaleUnclaimed(s);
                return (
                  <button
                    key={s.id}
                    type="button"
                    className="v2-sales-tbl-card"
                    onClick={() => setSelectedSale(s)}
                  >
                    <div className="v2-sales-tbl-card-top">
                      <strong>{unclaimed ? 'Unclaimed' : (s.recipientName || s.buyerName || 'Sale')}</strong>
                      <strong>{fmtMoney(s.paidAmount, currency)}</strong>
                    </div>
                    <div className="v2-sales-tbl-card-bottom">
                      <span className="v2-sales-tbl-sub">{voucher?.name || s.voucherId} · {s.code}</span>
                      <span
                        className="v2-sales-tbl-pill"
                        style={{
                          background: unclaimed ? '#fef3c7' : status.bg,
                          color: unclaimed ? '#b45309' : status.color,
                        }}
                      >
                        {unclaimed ? 'Unclaimed' : status.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {tableRows.length < rowsTotal && (
              <div className="v2-sales-table-footer">
                <button
                  type="button"
                  className="v2-sales-loadmore-btn"
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                  <span className="v2-sales-loadmore-meta">{rowsTotal - tableRows.length} remaining</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Per-sale detail slide-out */}
      {selectedSale && (
        <SalesDetailPanel
          sale={selectedSale}
          voucher={selectedVoucher}
          design={selectedDesign}
          salonName={salonName}
          currency={currency}
          onClose={() => setSelectedSale(null)}
          onResendEmail={onResendEmail}
          onAttachClient={async (...args) => {
            const out = onAttachClient ? await onAttachClient(...args) : undefined;
            setRefreshTick((t) => t + 1); // re-pull rows + aggregates
            return out;
          }}
        />
      )}
    </>
  );
};

export default SalesPage;
